/**
 * "Needs a decision" (T387 M6, rates-386 D3's queue as the composed cover):
 * every card is a decision with its EVIDENCE on it — who, when, from which
 * booking, how often asked. Card kinds: provisional (ratify / look first),
 * miss aggregate (put it on the list / not now), venue evidence (put it on
 * the list), Done-screen suggestions (put it on the list / not now).
 * Resolving the last card lands the "Nothing waiting" good-news state.
 */
import { useState } from 'preact/hooks';
import { EmptyState } from '@ecmb/ui';
import { PRICE_ENTRY_KIND_BY_SERVICE, type ServiceId } from '../../../shared/service-schema.js';
import {
  ratifyRate,
  dismissMiss,
  dismissSuggestion,
  type ApiQueue,
  type ApiQueueEvidence,
  type ApiQueueMiss,
  type ApiQueueSuggestion,
  type PriceEntryKind,
} from '../../lib/actions.js';
import { euro, shortDate } from '../../lib/format.js';
import { Banner } from '../../components/Banner.js';
import { COVER_NAMES } from './Shelf.js';

/** What the promote form opens with (evidence pre-fill). */
export type PromoteSeed = {
  kind: PriceEntryKind;
  service: string;
  name: string;
  /** band → cents, from the evidence (unquoted bands honestly absent). */
  bandCents: Record<string, number>;
  sourceBookingId?: number;
  source?: { type: 'suggestion'; feedbackId: number } | { type: 'miss'; placeSlug: string };
};

export type QueueViewProps = {
  queue: ApiQueue;
  onBack: () => void;
  /** "Look first" / ratify-jump: open the list at the row. */
  onOpenList: (service: string, filter?: string) => void;
  onPromote: (seed: PromoteSeed) => void;
  /** Re-load rates + queue after any resolution. */
  onChanged: () => void;
};

/** kind for a miss's service — misses without a listable service promote as
 *  airport towns (the Howth case; local-other has no price list). */
function missPromoteService(service: string | null): string {
  if (service != null && PRICE_ENTRY_KIND_BY_SERVICE[service as ServiceId] != null) return service;
  return 'airport';
}

/**
 * The queue as RENDERED (M6 ux finding): a venue with both an evidence card
 * and a Done-screen suggestion coalesces into the suggestion card (which
 * already pre-fills from the evidence bands). Shared by the shelf count and
 * the view so "N waiting" always equals the cards on screen.
 */
export function visibleQueue(q: ApiQueue): ApiQueue {
  return {
    ...q,
    evidence: q.evidence.filter((e) => !q.suggestions.some(
      (s) => s.place != null && s.place.toLowerCase() === e.venue.toLowerCase()
        && missPromoteService(s.service) === e.service,
    )),
  };
}

/** Total waiting cards, post-coalesce. */
export function queueCardCount(q: ApiQueue): number {
  const v = visibleQueue(q);
  return v.provisional.length + v.misses.length + v.evidence.length + v.suggestions.length;
}

export function QueueView({ queue: rawQueue, onBack, onOpenList, onPromote, onChanged }: QueueViewProps) {
  const [ratifyingKey, setRatifyingKey] = useState<string | null>(null);
  const [ratifyNote, setRatifyNote] = useState('');
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const queue = visibleQueue(rawQueue);
  const total = queueCardCount(rawQueue);

  const commitRatify = async (service: string, locationId: string, band: string) => {
    if (!ratifyNote.trim()) {
      setNotice({ kind: 'error', text: 'A note is required to confirm a price.' });
      return;
    }
    const res = await ratifyRate({ service, locationId, band }, ratifyNote.trim());
    if (res.ok) {
      setRatifyingKey(null);
      setNotice({ kind: 'ok', text: 'Price confirmed.' });
      onChanged();
    } else {
      setNotice({ kind: 'error', text: res.message });
    }
  };

  const missCard = (m: ApiQueueMiss) => (
    <div key={`miss-${m.placeSlug}`} class="queue-card">
      <p class="queue-headline">{m.place}</p>
      <p class="queue-evidence">
        Searched and not found · asked {m.count} {m.count === 1 ? 'time' : 'times'} · last {shortDate(m.lastSeen)}
        {m.service ? ` · ${COVER_NAMES[m.service] ?? m.service}` : ''}
      </p>
      <div class="queue-actions">
        <button
          type="button" class="btn btn--secondary"
          aria-label={`Put ${m.place} on the list`}
          onClick={() => onPromote({
            kind: (PRICE_ENTRY_KIND_BY_SERVICE[missPromoteService(m.service) as ServiceId] ?? 'town') as PriceEntryKind,
            service: missPromoteService(m.service),
            name: m.place,
            bandCents: {},
            source: { type: 'miss', placeSlug: m.placeSlug },
          })}
        >
          Put it on the list
        </button>
        <button
          type="button" class="btn btn--quiet"
          onClick={() => void dismissMiss(m.placeSlug).then((ok) => {
            if (ok) onChanged();
            else setNotice({ kind: 'error', text: "Dismissing it didn't work. Try again in a moment." });
          })}
        >
          Not now
        </button>
      </div>
    </div>
  );

  // T897. Two roles that used to coincide and now can differ, deliberately:
  // `last <date>` is when the VENUE was last hand-priced (recency, why the card
  // is here); `from booking N` is the booking that supplied the PRICE shown. A
  // venue whose recent jobs were returns shows a fresh date beside an older
  // booking's price. Do not "align" them — they answer different questions.
  const evidenceCard = (e: ApiQueueEvidence) => {
    const priced = Object.entries(e.bands)
      .map(([band, cents]) => `${band} seats ${euro(cents)}`).join(' · ');
    return (
      <div key={`ev-${e.service}-${e.venue}`} class="queue-card">
        <p class="queue-headline">{e.venue}</p>
        <p class="queue-evidence">
          Hand-priced {e.count} {e.count === 1 ? 'time' : 'times'} · {COVER_NAMES[e.service] ?? e.service} · last {shortDate(e.lastSeen)}
          {e.sourceBookingId != null && ` · from booking ${e.sourceBookingId}`}
          {priced !== '' && ` · ${priced}`}
          {/* Prices are per journey each way, so a total that covered a return
              leg or an add-on cannot set one. Say how many, rather than quietly
              showing fewer prices than "Hand-priced N times" implies. */}
          {e.excludedCount > 0 &&
            ` · ${e.excludedCount} with a return or add-ons${priced === '' ? ', so set the each-way price yourself' : ''}`}
        </p>
        <div class="queue-actions">
          <button
            type="button" class="btn btn--secondary"
            aria-label={`Put ${e.venue} on the list`}
            onClick={() => onPromote({
              kind: 'venue',
              service: e.service,
              name: e.venue,
              bandCents: e.bands,
              // Same spread-guard as the suggestion card below: PromoteSeed
              // keeps `sourceBookingId?: number`, so an absent provenance is
              // omitted rather than widening the seed to accept null.
              ...(e.sourceBookingId != null ? { sourceBookingId: e.sourceBookingId } : {}),
            })}
          >
            Put it on the list
          </button>
        </div>
      </div>
    );
  };

  const suggestionCard = (s: ApiQueueSuggestion) => {
    // SAME convention as the miss cards (M6 gate defect 1): an unmapped
    // service (local-other has no price list) promotes as an airport TOWN —
    // never a wedding venue. A local-other place suggestion must land on the
    // airport list AND capture into the gazetteer, exactly like its miss twin.
    const service = missPromoteService(s.service);
    // A suggestion's venue may already have evidence bands — pre-fill from
    // them. Join against the RAW queue: the coalesce hides the twin evidence
    // CARD precisely because the suggestion card carries its bands.
    const evidence = rawQueue.evidence.find(
      (e) => e.service === service && s.place != null && e.venue.toLowerCase() === s.place.toLowerCase(),
    );
    return (
      <div key={`sug-${s.feedbackId}`} class="queue-card">
        <p class="queue-headline">{s.place ?? 'Suggestion'}</p>
        <p class="queue-evidence">
          Suggested by {s.createdBy ?? 'the team'}, {shortDate(s.createdAt)}
          {s.bookingId != null ? ` · from booking ${s.bookingId}` : ''}
          {s.note ? ` · ${s.note}` : ''}
        </p>
        <div class="queue-actions">
          <button
            type="button" class="btn btn--secondary"
            aria-label={s.place != null ? `Put ${s.place} on the list` : 'Put it on the list'}
            disabled={s.place == null}
            onClick={() => s.place != null && onPromote({
              kind: (PRICE_ENTRY_KIND_BY_SERVICE[service as ServiceId] ?? 'town') as PriceEntryKind,
              service,
              name: s.place,
              // Prefer a venue-evidence twin's bands; else the source-booking
              // pre-fill fallback (server Arm 4) so a town/estimate suggestion
              // still opens pre-filled instead of empty. `?? {}` keeps the seed
              // a real Record when neither source has bands.
              bandCents: evidence?.bands ?? s.bandCents ?? {},
              ...(s.bookingId != null ? { sourceBookingId: s.bookingId } : {}),
              source: { type: 'suggestion', feedbackId: s.feedbackId },
            })}
          >
            Put it on the list
          </button>
          <button
            type="button" class="btn btn--quiet"
            onClick={() => void dismissSuggestion(s.feedbackId).then((ok) => {
              if (ok) onChanged();
              else setNotice({ kind: 'error', text: "Dismissing it didn't work. Try again in a moment." });
            })}
          >
            Not now
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div class="book-list-head">
        <button type="button" class="btn btn--quiet" onClick={onBack}>All price lists</button>
        {/* A2-pricelists-2 (T1723 M3): an `h1`. Live `main h1` count on this
            sub-view was ZERO. `Admin.tsx` renders its own `h1` only on
            `view.kind === 'shelf'`, so this screen has no competing one. */}
        <h1 class="screen-title">Needs a decision</h1>
        <span class="list-count">{total} waiting</span>
      </div>

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {/* ⭐ T1749 M9 (DS-25) — THE LIBRARY'S `EmptyState` (§7.1: a headline, one
          line of evidence, and the `role="status"` a bare `<div>` never had, so
          the sentence is ANNOUNCED when it replaces the cards). This was one of
          the two remaining `.queue-card--empty` sites; the class retires with
          them and its legacy entry arms.
          ⛔ BOTH SENTENCES ARE VERBATIM. `admin-screens.spec.tsx` is text-keyed
          on `Nothing waiting` and on `/has been looked at/`, and the first is
          also the shelf cover's chip text — the queue says the same thing in the
          same words from both doors, which is the property, not a coincidence.
          ⛔⛔ T1749 M9 rework 2 — `ok` IS NOW ASKED FOR, and this is one of the two
          screens in either tree that asks. The default became `neutral` because an
          empty list is not good news; THE REASON THIS ONE IS: this is a queue of
          WORK, and its emptiness means the work is done — every captured price,
          unlisted place and suggestion has been looked at, which is what the
          evidence line below says in words. Green here is a report on Helen's own
          queue, not a verdict on the business. */}
      {total === 0 && (
        <EmptyState
          tone="ok"
          headline="Nothing waiting"
          evidence="Every captured price, unlisted place and suggestion has been looked at."
        />
      )}

      {queue.provisional.map((p) => {
        const pricedCells = p.cells.filter((c) => c.cents != null);
        const single = pricedCells.length === 1 ? pricedCells[0] : null;
        const key = `${p.service}|${p.locationId}`;
        return (
          <div key={`prov-${key}`} class="queue-card queue-card--gold">
            <p class="queue-headline">{p.label}</p>
            <p class="queue-evidence">
              Awaiting confirmation · {COVER_NAMES[p.service] ?? p.service}
              {' · '}
              {pricedCells.map((c) => `${c.band} seats ${c.cents != null ? euro(c.cents) : ''}`).join(' · ')}
            </p>
            {ratifyingKey === key && single != null ? (
              <div class="ratify-panel">
                <p class="ratify-q">
                  Make {single.cents != null ? euro(single.cents) : 'this'} the standing {p.label} price for {single.band} seats?
                </p>
                <div class="ratify-row">
                  <input
                    class="login-input" type="text"
                    placeholder="Note, for the record. For example: checked against the last three jobs."
                    aria-label={`Reason to confirm ${p.label} ${single.band}`}
                    value={ratifyNote} onInput={(e) => setRatifyNote((e.target as HTMLInputElement).value)}
                    ref={(el) => { if (el && !el.dataset['autofocused']) { el.dataset['autofocused'] = '1'; el.focus(); } }}
                  />
                  <button type="button" class="btn btn--primary btn--inline" onClick={() => void commitRatify(p.service, p.locationId, single.band)}>Confirm price</button>
                  <button type="button" class="btn btn--quiet btn--inline" onClick={() => setRatifyingKey(null)}>Not now</button>
                </div>
              </div>
            ) : (
              <div class="queue-actions">
                {/* ⭐ T1723 M3 (K4): QUIET, not `btn--secondary`, and the same
                    reasoning as the rate list's per-band pills one file over. A
                    navy-filled `Confirm price` on a gold card competes with the
                    gold commit this card's own panel puts in its place a click
                    later, so the card presented two "strong" weights for two
                    stages of ONE act. `Look first` beside it is already quiet;
                    the pair now reads as two ways on rather than as a ranking.
                    ⚠️ The accessible name is untouched, so
                    `admin-screens.spec.tsx`'s `getByRole` walk is unaffected -
                    and this control and the panel's commit are never on screen
                    at the same time (the panel REPLACES this block), which is
                    what keeps "exactly one bare-named Confirm price" true. */}
                <button
                  type="button" class="btn btn--quiet"
                  onClick={() => {
                    // One priced cell → ratify right here; several → the list
                    // is the honest place to ratify cell-by-cell.
                    if (single != null) { setRatifyingKey(key); setRatifyNote(''); setNotice(null); }
                    else onOpenList(p.service, p.label);
                  }}
                >
                  Confirm price
                </button>
                <button type="button" class="btn btn--quiet" onClick={() => onOpenList(p.service, p.label)}>
                  Look first
                </button>
              </div>
            )}
          </div>
        );
      })}

      {queue.misses.map(missCard)}
      {queue.evidence.map(evidenceCard)}
      {queue.suggestions.map(suggestionCard)}
    </>
  );
}
